import React from "react";
import dynamic from "next/dynamic";

import getRegionComplianceRules from "api/requests/consumerApi/getRegionComplianceRules";
import { getDispensary } from "api/requests/getDispensary";
import getMenuItem from "api/requests/getMenuItem";
import getMenuItems, { GetMenuItemsReturn } from "api/requests/getMenuItems";
import getSimilarMenuItems from "api/requests/getSimilarMenuItems";
import { DISPENSARY_PAGES } from "constants/dispensary";
import { MenuProvider } from "context/MenuContext";
import { Dispensary } from "custom-types/Dispensary";
import { MenuItem } from "custom-types/MenuItem";
import { dispensaryMenuItemMetaData } from "lib/dispensaryMetadata";
import { createGetServerSideProps } from "utils/createGetServerSideProps";
import isDispensaryDualLicense from "utils/dispensary/isDispensaryDualLicense";
import generateDispensaryRedirect from "utils/generateDispensaryRedirect";
import { getMenuTypeCookie } from "utils/menuTypeUtils";
import { getCountryCodeHeader } from "utils/requestContext";
import useTrackDispensaryPageViewSplitEvent from "utils/split/useTrackDispensaryPageViewSplitEvent";

import DispensaryHeader from "components/Dispensary/DispensaryHeader";
import DispensaryTabs from "components/Dispensary/DispensaryTabs";
import MenuItemPage from "components/Dispensary/MenuItemPage";
import MetaTags from "components/MetaTags";

const MenuItem404Page = dynamic(
  () => import("components/Dispensary/MenuItemPage/404"),
);

type PdpPageProps = {
  dispensary: Dispensary;
  menuData?: GetMenuItemsReturn;
  menuItem?: MenuItem;
  similarMenuItems?: MenuItem[];
};

const PdpPage: React.FC<PdpPageProps> = ({
  dispensary,
  menuItem,
  menuData,
  similarMenuItems,
}) => {
  useTrackDispensaryPageViewSplitEvent(
    !!dispensary && !!menuItem
      ? {
          page: "pdp",
          tier: dispensary.featureTier,
        }
      : undefined,
  );

  if (!menuItem) {
    return <MenuItem404Page dispensary={dispensary} />;
  } else {
    const { coverPhotoUrl, logoUrl, name, tags } = dispensary;

    //used for all menu item pages (dispensary, CA, cbd)
    const { description, image, title } = dispensaryMenuItemMetaData(
      coverPhotoUrl,
      menuItem.brandName,
      logoUrl,
      menuItem.name,
      name,
    );

    const isDualLicense = isDispensaryDualLicense(tags);

    return (
      <>
        <MetaTags
          description={description}
          image={image}
          robots="noindex,noarchive"
          shouldShowAlternates={false}
          title={title}
        />
        <MenuProvider
          isDualLicenseDispensary={isDualLicense}
          initialData={menuData}
        >
          <DispensaryHeader dispensary={dispensary} />
          <section>
            <DispensaryTabs
              activeTab={DISPENSARY_PAGES.menu}
              dispensary={dispensary}
            />
            <MenuItemPage
              dispensary={dispensary}
              menuItem={menuItem}
              similarMenuItems={similarMenuItems}
            />
          </section>
        </MenuProvider>
      </>
    );
  }
};

export const getServerSideProps = createGetServerSideProps<PdpPageProps>(
  // @ts-expect-error -- TODO: Fix return type to adhere to props shape -- https://leafly.atlassian.net/browse/NL-1564
  async (context) => {
    const { query, req, resolvedUrl } = context;
    const { slug: dispensarySlug, menuItem: menuItemId } = query;

    const dispensary = await getDispensary(dispensarySlug as string);

    const domainCountryCode = getCountryCodeHeader(req.headers);

    const redirect = generateDispensaryRedirect(
      domainCountryCode,
      resolvedUrl,
      query,
      dispensary,
    );

    if (redirect) {
      return { redirect };
    }

    const menuTypeCookie = getMenuTypeCookie(req);
    const prefersMedical = menuTypeCookie === "Med";
    const isDualLicenseDispensary = isDispensaryDualLicense(
      dispensary?.tags || [],
    );

    const getMenuItemCall = getMenuItem(parseInt(menuItemId as string, 10));

    const getMenuItemsCall = getMenuItems(dispensarySlug as string, {
      perPage: 0,
      ...(isDualLicenseDispensary && {
        menu_type: prefersMedical ? "Med" : "Rec",
      }),
    });
    const getSimilarItemsCall = getSimilarMenuItems(menuItemId as string, {
      dispensarySlug: dispensarySlug as string,
      ...(isDualLicenseDispensary && {
        medical: `${prefersMedical}`,
      }),
    });

    const getComplianceRulesCall = getRegionComplianceRules(
      dispensary?.state,
      dispensary?.country,
    );

    const [menuItem, menuData, similarMenuItems, complianceRuleset] =
      await Promise.all([
        getMenuItemCall,
        getMenuItemsCall,
        getSimilarItemsCall,
        getComplianceRulesCall,
      ]);

    return {
      props: {
        complianceRuleset,
        dispensary,
        menuData,
        menuItem: menuItem || null,
        similarMenuItems,
      },
    };
  },
);

export default PdpPage;
